import type { IPatentResponse, ILiteratureResponse } from '@/composables/graph/detail/use-workspaces';
import { PsHttpService } from '@patsnap/tahiti-core';
import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface IWSTaskListParams {
    type: string;
    page: number;
    limit: number;
    status: string;
}

export interface IWSTaskListResponse {
    page: number;
    limit: number;
    total: number;
    rows: any[];
}

export interface WorkspaceApi {
    getWorkspacePatents(spaceId: string, folderId: string, page: number, limit: number, sort?: string): Promise<IPatentResponse>;
    getWorkspaceData(spaceId: string, folderId: string, page: number, limit: number, sort?: string, dataType?: string): Promise<ILiteratureResponse>;
    deleteWorkspacePatents(spaceId: string, folderId: string, patentIds: string[]): Promise<void>;
    deleteWorkspaceData(folderId: string, docIds: string[], dataType: string): Promise<void>;
    getTaskList(params: IWSTaskListParams): Promise<IWSTaskListResponse>;
}

@injectable()
export class WorkspaceApiImpl extends PsHttpService implements WorkspaceApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_WORKSPACE_SERVICE_URI}/workspace`,
            headers: {
                'X-Api-Version': '2.0',
            },
        }
    }

    getWorkspacePatents(spaceId: string, folderId: string, page: number, limit: number, sort: string = 'wtasc'): Promise<IPatentResponse> {
        return this.post<PatSnapCommonResponseData<IPatentResponse>>({
            url: `/web/${spaceId}/folder/${folderId}/patents`,
            data: {
                page,
                sort,
                view_type: 'standardlist',
            },
        }).then((res) => {
            return res.data as IPatentResponse;
        }).catch(() => {
            return {
                num: 0,
                total: 0,
                page,
                limit,
                patents_data: [],
            } as IPatentResponse;
        });
    }

    getWorkspaceData(spaceId: string, folderId: string, page: number, limit: number, sort: string = 'wtasc', dataType = 'PAPER'): Promise<ILiteratureResponse> {
        return this.post<PatSnapCommonResponseData<ILiteratureResponse>>({
            url: `/web/${spaceId}/folder/${folderId}/data`,
            data: {
                page,
                sort,
                data_type: dataType,
                view_type: 'tablelist',
            },
        }).then((res) => {
            return res.data as ILiteratureResponse;
        }).catch(() => {
            return {
                num: 0,
                total: 0,
                page,
                limit,
                details: [],
            } as ILiteratureResponse;
        });
    }

    deleteWorkspacePatents(spaceId: string, folderId: string, patentIds: string[]): Promise<void> {
        return this.delete<PatSnapCommonResponseData<void>>({
            url: `/web/space/${spaceId}/folder/${folderId}/delete-patents`,
            data: {
                family_type: null,
                patent_ids: patentIds,
                refiner: {},
                select_mode: 'INCLUDE',
                sort: 'wtasc',
            },
        }).then(() => {

        });
    }

    deleteWorkspaceData(folderId: string, docIds: string[], dataType: string = 'PAPER'): Promise<void> {
        return this.delete<PatSnapCommonResponseData<void>>({
            url: `/web/folder/${folderId}/delete-data`,
            data: {
                data_type: dataType,
                doc_ids: docIds,
                refiner: {},
                select_mode: 'INCLUDE',
                sort: 'wtasc',
            },
        }).then(() => {
        });
    }

    getTaskList(params: IWSTaskListParams): Promise<IWSTaskListResponse> {
        return this.get<PatSnapCommonResponseData<IWSTaskListResponse>>({
            url: '/web/task-center/list',
            params: {
                type: params.type,
                page: params.page,
                limit: params.limit,
                status: params.status,
            },
        }).then((res) => {
            return res.data as IWSTaskListResponse;
        }).catch(() => {
            return {
                page: params.page,
                limit: params.limit,
                total: 0,
                rows: [],
            } as IWSTaskListResponse;
        });
    }
}

export const WorkspaceApi = Symbol('WorkspaceApi');
